import { Component, Inject, OnDestroy, OnInit } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { Subscription } from 'rxjs';
import { TranslateService } from '@ngx-translate/core';

import { BikesService } from '../../services/bikes.service';
import { SignalRService } from '../../services/signalr.service';
import { LoggerService } from '../../services/logger.service';
import { BikeDisableState } from '../../core/enums/bikeDisableState';
import { SignalRSubscriber } from '../../core/models/live/signalr-subscriber';

const DPHWID_OBSERVATION_ID = 450;

export interface PostRepairStatusPopupData {
  bikeId: number;
}

export interface BikeStateChange {
  Disabled: boolean;
  DisabledReason?: BikeDisableState;
}

@Component({
  selector: 'app-post-repair-status-popup',
  templateUrl: './post-repair-status-popup.component.html',
  styleUrls: ['./post-repair-status-popup.component.scss']
})
export class PostRepairStatusPopupComponent implements OnInit, OnDestroy {

  bikeId: number;
  serial: string;
  isSubmitting = false;
  optionSelected = false;
  errorMessage: string | null = null;

  private signalRSubscription: Subscription;
  private bikeDetailsSubscription: Subscription;

  constructor(
    public dialogRef: MatDialogRef<PostRepairStatusPopupComponent>,
    @Inject(MAT_DIALOG_DATA) public data: PostRepairStatusPopupData,
    private bikesService: BikesService,
    private signalRService: SignalRService,
    private loggerService: LoggerService,
    private translate: TranslateService
  ) { }

  ngOnInit() {
    this.bikeId = this.data.bikeId;
    this.bikeDetailsSubscription = this.bikesService.getBikeDetails(this.bikeId).subscribe(bike => {
      this.serial = bike.Serial;
      this.signalRService.SubscribeConnection(this.serial);
      this.signalRSubscription = this.signalRService.signalReceived.subscribe(
        (update: SignalRSubscriber) => this.onSignalRUpdate(update)
      );
    }, () => {
      this.loggerService.showErrorMessage(this.translate.instant('REPAIR_ACTION.NEXT_STATUS_WATCH_FAILED'));
    });
  }

  selectMoving() {
    this.chooseOption({ Disabled: true, DisabledReason: BikeDisableState.Moving });
  }

  selectToWorkshop() {
    this.chooseOption({ Disabled: true, DisabledReason: BikeDisableState.ToWorkshop });
  }

  selectDeliverOutsideDockingStation() {
    this.chooseOption({ Disabled: false });
  }

  private chooseOption(bikeStateChange: BikeStateChange) {
    if (this.isSubmitting) {
      return;
    }
    this.optionSelected = true;
    this.submit(bikeStateChange);
  }

  private onSignalRUpdate(update: SignalRSubscriber) {
    if (this.optionSelected) {
      return;
    }
    if (update.serial !== this.serial || update.id !== DPHWID_OBSERVATION_ID) {
      return;
    }
    const isDocked = update.value != null && update.value !== '';
    if (isDocked) {
      this.optionSelected = true;
      this.submit({ Disabled: false });
    }
  }

  private submit(bikeStateChange: BikeStateChange) {
    this.isSubmitting = true;
    this.errorMessage = null;
    this.bikesService.enableDisableBike(this.bikeId, bikeStateChange).subscribe(() => {
      this.isSubmitting = false;
      // Close with the applied state change (rather than a bare `true`) so callers can
      // update the bike they're already displaying immediately, without waiting for a refresh.
      this.dialogRef.close(bikeStateChange);
    }, () => {
      this.isSubmitting = false;
      this.errorMessage = this.translate.instant('REPAIR_ACTION.NEXT_STATUS_UPDATE_FAILED');
    });
  }

  ngOnDestroy() {
    if (this.bikeDetailsSubscription) {
      this.bikeDetailsSubscription.unsubscribe();
    }
    if (this.signalRSubscription) {
      this.signalRSubscription.unsubscribe();
    }
    if (this.serial) {
      this.signalRService.UnSubscribeConnection(this.serial);
    }
  }
}
